import React, { useState, useEffect } from 'react';
import './CategoryManager.css';
import { apiService } from '../services/api';

const CategoryManager = () => {
  const [categories, setCategories] = useState({});
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryInsights, setCategoryInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: '', description: '' });
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [analysisStatus, setAnalysisStatus] = useState({});

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const response = await apiService.getCategories();
      setCategories(response.data);
    } catch (error) {
      console.error('Error loading categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyzeCategory = async (categoryName, analyzeNewOnly = false) => {
    try {
      setAnalysisStatus(prev => ({ ...prev, [categoryName]: 'analyzing' }));
      
      const response = await apiService.analyzeCategory(categoryName, 'single_prompt', analyzeNewOnly);
      const result = response.data;
      
      if (result.status === 'success') {
        setAnalysisStatus(prev => ({ ...prev, [categoryName]: 'completed' }));
        // Reload categories to update last_analyzed
        await loadCategories();
        
        // If this category is selected, reload its insights
        if (selectedCategory === categoryName) {
          await loadCategoryInsights(categoryName);
        }
      } else {
        setAnalysisStatus(prev => ({ ...prev, [categoryName]: 'failed' }));
      }
    } catch (error) {
      console.error('Error analyzing category:', error);
      setAnalysisStatus(prev => ({ ...prev, [categoryName]: 'failed' }));
    }
  };

  const loadCategoryInsights = async (categoryName) => {
    try {
      const response = await apiService.getCategoryInsights(categoryName);
      setCategoryInsights(response.data);
    } catch (error) {
      console.error('Error loading category insights:', error);
    }
  };

  const handleCategorySelect = async (categoryName) => {
    setSelectedCategory(categoryName);
    await loadCategoryInsights(categoryName);
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    try {
      const response = await apiService.createCategory(newCategory.name, newCategory.description);
      const result = response.data;
      if (result.status === 'success') {
        setNewCategory({ name: '', description: '' });
        setShowCreateForm(false);
        await loadCategories();
      } else {
        alert('Error creating category: ' + result.error);
      }
    } catch (error) {
      console.error('Error creating category:', error);
      alert('Error creating category');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'green';
      case 'pending': return 'orange';
      case 'empty': return 'gray';
      case 'analyzing': return 'blue';
      case 'failed': return 'red';
      default: return 'gray';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed': return '✓ Analyzed';
      case 'pending': return '⏳ Pending';
      case 'empty': return '📁 Empty';
      case 'analyzing': return '🔄 Analyzing...';
      case 'failed': return '❌ Failed';
      default: return 'Unknown';
    }
  };

  return (
    <div className="category-manager">
      <div className="category-header">
        <h1>Category Manager</h1>
        <button 
          className="create-category-btn"
          onClick={() => setShowCreateForm(!showCreateForm)}
        >
          {showCreateForm ? 'Cancel' : '+ Create Category'}
        </button>
      </div>

      {/* Create Category Form */}
      {showCreateForm && (
        <div className="create-category-form">
          <h3>Create New Category</h3>
          <form onSubmit={handleCreateCategory}>
            <div className="form-group">
              <label>Category Name:</label>
              <input
                type="text"
                value={newCategory.name}
                onChange={(e) => setNewCategory(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., leadership_development"
                required
              />
            </div>
            <div className="form-group">
              <label>Description:</label>
              <textarea
                value={newCategory.description}
                onChange={(e) => setNewCategory(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Optional description of this category"
              />
            </div>
            <button type="submit" className="submit-btn">Create Category</button>
          </form>
        </div>
      )}

      <div className="category-content">
        {/* Categories List */}
        <div className="categories-list">
          <h2>Categories ({Object.keys(categories).length})</h2>
          {loading ? (
            <div className="loading">Loading categories...</div>
          ) : (
            <div className="category-cards">
              {Object.entries(categories).map(([categoryName, categoryInfo]) => (
                <div 
                  key={categoryName}
                  className={`category-card ${selectedCategory === categoryName ? 'selected' : ''}`}
                  onClick={() => handleCategorySelect(categoryName)}
                >
                  <div className="category-header">
                    <h3>{categoryInfo.display_name}</h3>
                    <span 
                      className={`status-badge status-${getStatusColor(categoryInfo.analysis_status || analysisStatus[categoryName])}`}
                    >
                      {getStatusText(categoryInfo.analysis_status || analysisStatus[categoryName])}
                    </span>
                  </div>
                  
                  <p className="category-description">{categoryInfo.description}</p>
                  
                  <div className="category-stats">
                    <span>📄 {categoryInfo.proposal_count} proposals</span>
                    {categoryInfo.last_analyzed && (
                      <span>🕒 {new Date(categoryInfo.last_analyzed).toLocaleDateString()}</span>
                    )}
                  </div>
                  
                  <div className="category-actions">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAnalyzeCategory(categoryName, false);
                      }}
                      disabled={analysisStatus[categoryName] === 'analyzing'}
                      className="analyze-btn"
                    >
                      {analysisStatus[categoryName] === 'analyzing' ? 'Analyzing...' : 'Analyze All'}
                    </button>
                    
                    {categoryInfo.proposal_count > 0 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAnalyzeCategory(categoryName, true);
                        }}
                        disabled={analysisStatus[categoryName] === 'analyzing'}
                        className="analyze-new-btn"
                      >
                        Analyze New Only
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Category Insights */}
        {selectedCategory && categoryInsights && (
          <div className="category-insights">
            <h2>{categories[selectedCategory]?.display_name} - Insights</h2>
            
            {categoryInsights.error ? (
              <div className="error-message">
                {categoryInsights.error}
              </div>
            ) : (
              <div className="insights-content">
                <div className="insights-summary">
                  <h3>Summary</h3>
                  <p>Total Proposals: {categoryInsights.total_proposals || 0}</p>
                  <p>Analysis Date: {categoryInsights.analysis_timestamp ? 
                    new Date(categoryInsights.analysis_timestamp).toLocaleString() : 'N/A'}</p>
                </div>

                {categoryInsights.proposals_analyzed && (
                  <div className="proposals-list">
                    <h3>Analyzed Proposals</h3>
                    <div className="proposal-items">
                      {categoryInsights.proposals_analyzed.map((proposal, index) => (
                        <div key={index} className="proposal-item">
                          <h4>{proposal.proposal_name || proposal.source_file}</h4>
                          {proposal.strategic_context && (
                            <div className="strategic-context">
                              <strong>Summary:</strong> {proposal.strategic_context.proposal_summary}
                            </div>
                          )}
                          {proposal.games_and_activities && proposal.games_and_activities.length > 0 && (
                            <div className="games-section">
                              <strong>Games/Activities:</strong> {proposal.games_and_activities.length} found
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {categoryInsights.combined_insights && (
                  <div className="combined-insights">
                    <h3>Combined Insights</h3>
                    <div className="insights-grid">
                      {categoryInsights.combined_insights.content_themes && (
                        <div className="insight-section">
                          <h4>Content Themes</h4>
                          <ul>
                            {categoryInsights.combined_insights.content_themes.slice(0, 5).map((theme, index) => (
                              <li key={index}>{theme}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {categoryInsights.combined_insights.common_patterns && (
                        <div className="insight-section">
                          <h4>Common Patterns</h4>
                          <ul>
                            {categoryInsights.combined_insights.common_patterns.slice(0, 5).map((pattern, index) => (
                              <li key={index}>{pattern}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryManager; 